import React, { useCallback } from "react";
import { Transition } from "@headlessui/react";
import Loading from "../../common/components/loading";
import Error from "../../common/components/error";
import { useRmnMlsRequestsQuery } from "../../graphql/generated";
import { useTitle } from "../../common/utils/title";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import PageHeader from "../../common/components/page-header";

const RmnMlsRequestList: React.FC = () => {
  useTitle("RMN MLS Requests");

  const [{ data, error, fetching }] = useRmnMlsRequestsQuery({});

  const downloadCsv = useCallback(() => {
    if (!data?.rmnMlsRequests) return;

    const headers = [
      "ID",
      "Created At",
      "First Name",
      "Last Name",
      "MLS",
      "Email",
      "Member MLS ID",
      "Phone",
    ];

    const escapeField = (field: string | null | undefined): string => {
      if (field == null) return "";
      if (field.includes(",") || field.includes('"') || field.includes("\n")) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    const rows = data.rmnMlsRequests.map((r) =>
      [
        r.id,
        r.createdAt,
        r.firstName,
        r.lastName,
        r.mls,
        r.email,
        r.memberMlsId,
        r.phone,
      ]
        .map(escapeField)
        .join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "rmn-mls-requests.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [data]);

  return (
    <>
      <Loading show={!data && !error} />
      <Error error={error} />
      <Transition
        show={!!data}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <PageHeader>
          <PageHeader.Title>
            RMN MLS Requests
            <span className="text-sm text-gray-500 px-2">
              {fetching
                ? "Loading..."
                : `${data?.rmnMlsRequests.length.toLocaleString()}`}
            </span>
          </PageHeader.Title>
          <PageHeader.Actions>
            <button
              type="button"
              onClick={downloadCsv}
              disabled={!data?.rmnMlsRequests?.length}
              className="inline-flex items-center rounded-md bg-zenlist-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-zenlist-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
              Download CSV
            </button>
          </PageHeader.Actions>
        </PageHeader>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      MLS
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member MLS ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.rmnMlsRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {request.firstName} {request.lastName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {request.mls}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {request.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {request.memberMlsId ?? "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {request.phone ?? "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default RmnMlsRequestList;
